import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocuments } from '../firebase/firestore';
import BlogEditor from '../components/blog/BlogEditor';
import MarkdownUploader from '../components/blog/MarkdownUploader';
import { useAdmin } from '../hooks/useAdmin';
import { addDocument, updateDocument, deleteDocument } from '../firebase/firestore';

const AdminBlog = () => {
  const navigate = useNavigate();
  const {loading, isAdmin, logout } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsError, setPostsError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showUploader, setShowUploader] = useState(false);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [loading, isAdmin, navigate]);

  // load articles from Firestore when component mounts
  useEffect(() => {
    const fetchArticles = async () => {
      setLoadingPosts(true);
      setPostsError(null);
      try {
        const docs = await getDocuments('articles');
        // sort newest first
        docs.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setPosts(docs);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setPostsError(err);
      } finally {
        setLoadingPosts(false);
      }
    };

    if (isAdmin) {
      fetchArticles();
    }
  }, [isAdmin]);

  const handleSave = async (data, options = {}) => {
    const published = typeof options.published !== 'undefined' ? options.published : (data.published || false);
    const item = {
      ...data,
      published,
      date: data.date || new Date().toISOString().slice(0, 10),
    };

    try {
      if (editingPost && editingPost.id) {
        // update existing document
        await updateDocument('articles', editingPost.id, item);
        setPosts((prev) => prev.map((p) => (p.id === editingPost.id ? { ...p, ...item } : p)));
      } else {
        // create new document
        const docId = await addDocument('articles', item);
        setPosts((prev) => [{ id: docId, ...item }, ...prev]);
      }
    } catch (err) {
      console.error('Firestore save error:', err);
      alert('Failed to save to Firestore: ' + (err.message || err));
    }

    setShowEditor(false);
    setEditingPost(null);
  };

  const handleDelete = async (id) => {
    try {
      if (typeof id === 'string') {
        await deleteDocument('articles', id);
      }
      setPosts(posts.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete article: ' + (err.message || err));
    }
  };

  const handleImport = async (articles) => {
    if (!articles || !articles.length) return;
    try {
      const addPromises = articles.map((a) => addDocument('articles', a));
      const ids = await Promise.all(addPromises);
      const withIds = articles.map((a, idx) => ({ ...a, id: ids[idx] }));
      setPosts((prev) => [...withIds, ...prev]);
      setShowUploader(false);
      alert(`Imported ${withIds.length} article(s) to Firestore.`);
    } catch (err) {
      console.error('Import error:', err);
      alert('Failed to import articles to Firestore: ' + (err.message || err));
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-10 flex justify-between items-start gap-6">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage blog posts, create articles, and monitor your content</p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 font-semibold transition whitespace-nowrap h-fit"
          >
            Logout
          </button>
        </div>

        {showEditor && (
          <div className="mb-10 bg-white rounded-lg shadow-lg p-8 border-2 border-blue-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              {editingPost ? '✏️ Edit Article' : '+ Create New Article'}
            </h2>
            <BlogEditor
              onSave={handleSave}
              initial={editingPost || {}}
            />
            <button
              onClick={() => {
                setShowEditor(false);
                setEditingPost(null);
              }}
              className="mt-6 bg-gray-400 text-white px-6 py-3 rounded-lg hover:bg-gray-500 font-semibold transition"
            >
              Cancel
            </button>
          </div>
        )}

        {!showEditor && (
          <div className="mb-8 flex items-center gap-4">
            <button
              onClick={() => {
                setEditingPost(null);
                setShowEditor(true);
              }}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition flex items-center gap-2"
            >
              + New Article
            </button>
            <button
              onClick={() => setShowUploader((s) => !s)}
              className="ml-4 bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 font-semibold transition"
            >
              Import Markdown
            </button>
            {showUploader && (
              <div className="mt-6">
                <MarkdownUploader onImport={handleImport} />
              </div>
            )}
          </div>
        )}

        {/* Articles Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
            <h2 className="text-2xl font-bold">Published & Draft Articles ({posts.length})</h2>
          </div>
          <div className="p-6">
            {loadingPosts ? (
              <p className="text-center text-gray-600">Loading articles…</p>
            ) : postsError ? (
              <p className="text-center text-red-600">Failed to load articles.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Title</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Category</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Created</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-600">
                          No articles yet. Click "New Article" to create one!
                        </td>
                      </tr>
                    ) : (
                      posts.map((post) => (
                        <tr key={post.id} className="border-b border-gray-200 hover:bg-blue-50 transition">
                          <td className="px-6 py-4">
                            <div className="font-semibold text-gray-900">{post.title}</div>
                            <div className="text-xs text-gray-500">{post.slug}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700 bg-gray-100 px-3 py-1 rounded">
                              {post.category || 'Uncategorized'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                post.published
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {post.published ? '✓ Published' : '○ Draft'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {post.date ? new Date(post.date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-center space-x-3">
                            <button
                              onClick={() => {
                                setEditingPost(post);
                                setShowEditor(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-semibold transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="text-red-600 hover:text-red-800 font-semibold transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBlog;
